import type { FC, ReactNode, RefObject } from 'react'

import Link from 'next/link'
import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import Logo from '@/components/generals/logo'
import MainContainer from '@/components/generals/main-container'
import { siteConfig } from '@/constants/site'

interface MobileMenuProps {
    open: boolean
    onClose: () => void
    path: string
    returnFocusRef: RefObject<HTMLButtonElement | null>
}

const MobileMenu: FC<MobileMenuProps> = ({ open, onClose, path, returnFocusRef }): ReactNode => {
    const closeRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (!open) return

        const returnTarget = returnFocusRef.current
        const previousOverflow = document.body.style.overflow

        document.body.style.overflow = 'hidden'
        closeRef.current?.focus()

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }

        window.addEventListener('keydown', onKeyDown)

        return () => {
            document.body.style.overflow = previousOverflow
            window.removeEventListener('keydown', onKeyDown)
            returnTarget?.focus()
        }
    }, [open, onClose, returnFocusRef])

    if (!open) return null

    return (
        <div
            id='mobile-menu'
            role='dialog'
            aria-modal='true'
            aria-label='Site menu'
            className='fixed inset-0 z-50 flex flex-col overflow-y-auto bg-background animate-in fade-in duration-200 md:hidden'
        >
            <MainContainer className='flex h-16 shrink-0 items-center justify-between border-b border-border'>
                <Logo onNavigate={onClose} />

                <button
                    ref={closeRef}
                    type='button'
                    onClick={onClose}
                    className='-mr-2 inline-flex min-h-11 items-center gap-2 rounded-sm px-2 font-mono text-label uppercase'
                >
                    Close
                    <X aria-hidden='true' className='size-5' />
                </button>
            </MainContainer>

            <MainContainer as='nav' className='flex-1 py-6'>
                <ol aria-label='Main' className='border-t border-border'>
                    {siteConfig.nav.map((item, index) => {
                        const active = path === item.href || path.startsWith(`${item.href}/`)

                        return (
                            <li key={item.href} className='border-b border-border'>
                                <Link
                                    href={item.href}
                                    onClick={onClose}
                                    aria-current={active ? 'page' : undefined}
                                    className='group flex min-h-16 items-baseline justify-between gap-4 py-4'
                                >
                                    <span className={cn('font-heading text-[2.25rem] leading-none tracking-[-0.02em]', active && 'italic text-brand')}>
                                        {item.label}
                                    </span>

                                    <span className='font-mono text-label text-muted-foreground'>
                                        {String(index + 1).padStart(2, '0')}
                                    </span>
                                </Link>
                            </li>
                        )
                    })}
                </ol>

                <p className='mt-10 max-w-[30ch] font-heading text-xl italic leading-snug text-muted-foreground'>
                    {siteConfig.tagline}
                </p>

                <a href='/rss.xml' className='mt-6 inline-flex font-mono text-label uppercase text-muted-foreground link-underline'>
                    Subscribe via RSS
                </a>
            </MainContainer>
        </div>
    )
}

export default MobileMenu
